import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { AppText } from "@toothfairy/shared-ui";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import dictionariesHelper from "../../../../API/Dictionaries/dictionariesHelper";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import { DictionaryItem } from "../../../../models";
import { isLargeScreen } from "../../../MainViewContainer";
import DictionarySettingsAI from "./DictionarySettingsAI";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";

const DictionariesList = ({ isDictionariesLoading }) => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showDictionaryModal, setShowDictonaryModal] = React.useState(false);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedDictionaryId, setSelectedDictionaryId] = React.useState(null);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedDictionaries, setDisplayedDictionaries] = React.useState([]);
  const [selectedDictionaryIds, setSelectedDictionaryIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const [initialMapValues, setInitialMapValues] = useState({
    sourceLanguage: "en",
    targetLanguage: "en",
    sourceText: "",
    targetText: "",
    description: "",
    pos: "",
  });
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.sourceLanguage"),
    },
    {
      title: t("settings.list.sourceText"),
    },
    {
      title: t("settings.list.targetLanguage"),
    },
    {
      title: t("settings.list.targetText"),
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
    },
  ];
  const onMappingCreated = useCallback(
    (event) => {
      if (event === "success") {
        AppToasty.show(t("mappingCreationSuccessCaption"), {
          type: "success",
        });
        setShowDictonaryModal(false);
      } else if (event === "inconsistent_language_mapping")
        AppToasty.show(t("inconsistentLanguageMappingCaption"), {
          type: "danger",
        });
      else if (event === "en_language_not_found_mapping")
        AppToasty.show(t("enLanguageNotFoundMappingCaption"), {
          type: "danger",
        });
      else if (event === "en_language_mapping")
        AppToasty.show(t("enLanguageMappingCaption"), {
          type: "danger",
        });
      else
        AppToasty.show(t("mappingCreationFailedCaption"), {
          type: "danger",
        });
      setSelectedIndex(null);
      setSelectedDictionaryId(null);
    },
    [AppToasty, t, setShowDictonaryModal]
  );
  const loadDictionaries = async (workspaceID) => {
    try {
      setIsLoading(true);
      let dictionaries = await dictionariesHelper.getDictionariesByWorkspaceID(
        workspaceID
      );
      dictionaries = dictionaries.data?.dictionaryItemsByWorkspaceID?.items?.filter(
        (u) => u?._deleted != true
      );
      dispatch("SET_AI_DICTIONARIES_CONFIG", dictionaries);
      setDisplayedDictionaries(dictionaries);
    } catch (error) {
      console.error("Error loading dictionaries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDictionaries(state?.activeWorkspaceID);
  }, [state?.activeWorkspaceID]);
  useEffect(() => {
    setDisplayedDictionaries(state?.aiDictionariesConfig);
  }, [state?.aiDictionariesConfig]);

  const dictonaryItemCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          maxHeight: isLargeScreen() ? (isEditMode ? 720 : 680) : 640,
          height: "95%",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={showDictionaryModal}
        setIsModalOpen={setShowDictonaryModal}
        modalTitle={
          isEditMode
            ? t("settings.list.dictionaryEditTitle")
            : t("settings.list.dictionaryCreationTitle")
        }
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowDictonaryModal(false);
          setSelectedIndex(null);
          setSelectedDictionaryId(null);
        }}
      >
        <DictionarySettingsAI
          ref={formRef}
          initialMapValues={initialMapValues}
          onMappingCreated={onMappingCreated}
          isEdit={isEditMode}
          selectedId={selectedDictionaryId}
          setDisplayedView={setDisplayedDictionaries}
          setShowDictonaryModal={setShowDictonaryModal}
        />
      </ToothFairyModal>
    );
  }, [
    showDictionaryModal,
    setShowDictonaryModal,
    initialMapValues,
    isEditMode,
    selectedIndex,
    selectedDictionaryId,
    displayedDictionaries,
    setShowDictonaryModal,
  ]);

  const handleDictionaryItemSelection = ({ id, item, index }) => {
    setSelectedIndex(index);
    setSelectedDictionaryId(id);
    setIsEditMode(true);
    setInitialMapValues(item);
    setShowDictonaryModal(true);
  };
  const handleDictionaryItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedDictionaryId || selectedItem?.id;
      if (_id) {
        const result = await dictionariesHelper.deleteDictionaryItem(_id);
        dispatch("SET_AI_DICTIONARIES_CONFIG", [
          ...state?.aiDictionariesConfig.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("mappingDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedDictionaryId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      AppToasty.show(t("mappingDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedDictionaryIds) {
        await dictionariesHelper.deleteDictionaryItem(id);
      }
      dispatch("SET_AI_DICTIONARIES_CONFIG", [
        ...state?.aiDictionariesConfig.filter(
          (u) => !selectedDictionaryIds.includes(u.id)
        ),
      ]);
      setSelectedDictionaryIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("mappingDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("mappingDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    setInitialMapValues({
      sourceLanguage: "en",
      sourceText: "",
      targetLanguage: "en",
      targetText: "",
      description: "",
    });
    setIsEditMode(false);
    setShowDictonaryModal(true);
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.deleteDictionaryConfirmation")}
        caption={t("settings.list.deleteDictionaryConfirmation")}
        subCaption={t("settings.list.deleteDictionaryConfirmationMessage")}
        confirmCaption={t("settings.list.deleteDictionaryConfirmation")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleDictionaryItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);
  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteDictionaryConfirmation"),
        })}
        subCaption={t("settings.list.deleteDictionaryConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedDictionaryIds]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("settings.list.loadingDictionaries")}
        />
      ) : (
        <>
          {displayedDictionaries?.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("settings.toolbar.searchDictionaries")}
              onSelectionChange={(selectedItems) => {
                setSelectedDictionaryIds(
                  selectedItems?.map((u) => u?.id) || []
                );
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedDictionaryIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              actionButtons
              showDropDownMenuForActions
              mainActionLabel={t("settings.list.edit")}
              onClickMainAction={(item, index) => {
                handleDictionaryItemSelection({
                  id: item?.id,
                  item,
                  index,
                });
              }}
              onActionItemPress={(id, item, index) => {
                if (id === "EDIT") {
                  handleDictionaryItemSelection({
                    id: item?.id,
                    item,
                    index,
                  });
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setSelectedDictionaryId(item?.id);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              style={{
                zIndex: -1,
                marginTop: 15,
                height:
                  displayedDictionaries?.length >= 50
                    ? height - 95
                    : height - 75,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
              }}
              headers={columns}
              items={displayedDictionaries
                ?.sort((a, b) =>
                  (a?.sourceText || "").localeCompare(b?.sourceText || "")
                )
                ?.map((u) => {
                  return {
                    id: u?.id || " ",
                    sourceLanguage: u.sourceLanguage,
                    sourceText: u.sourceText,
                    targetLanguage: u.targetLanguage,
                    targetText: u.targetText,
                    description: u.description,
                    actions: " ",
                  };
                })}
            >
              <ToothFairyButton
                title={t("settings.toolbar.create")}
                onPress={handleCreation}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.language}
              mainCaption={t("settings.list.noDictionariesFound")}
              subCaption={t("settings.list.noDictionariesFoundSub")}
              actionLabel={t("settings.toolbar.create")}
              onActionPress={handleCreation}
            />
          )}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: -10,
            }}
          >
            <AppText
              style={{
                textDecoration: "underline",
                marginLeft: 5,
              }}
              iconSize={16}
              color={currentTheme?.theme?.font_color}
              leftIcon={AppAssets?.icons?.info}
            >
              {t("settings.list.dictionariesEffectWarning")}
            </AppText>
          </View>
        </>
      )}

      {dictonaryItemCreationComponent}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default DictionariesList;
